import React, { useEffect } from 'react'
import styled from 'styled-components'
import { connect, useDispatch } from 'react-redux'
import SubnavBar from 'Components/Shared/SubnavBar'
import { Row, Col } from 'antd'
import { RouteComponentProps } from 'react-router-dom'
import RootState from 'typings/RootState'
import { BreadcrumbLink } from 'Components/Shared/BreadCrumb/BreadCrumb'
import { selectedProjectCodeSelector } from 'features/project/project.selector'
import {
  userEmailSelector,
  serviceTokenSelector,
} from 'features/sensitive/sensitive.selector'
import {
  selectData,
  selectEvent,
  selectSorting,
  selectFilter,
} from 'features/cmpj/commissioning/dashboard/selector'
import {
  sortingDashboard,
  clearFilter,
} from 'features/cmpj/commissioning/dashboard/actions'
import { getCommissioningDashboard } from 'features/cmpj/commissioning/dashboard/api'
import {
  GetDashboardReq,
  TypeOfTask,
} from 'features/cmpj/commissioning/dashboard/types'
import { translate } from 'i18n'
import TKeys from 'i18n/translationKey'

import NoCard from 'Components/Shared/NoCard'
import Loading from 'Components/Shared/Loading'
import { ColumnHeader, LoadingView } from 'Components/Shared'
import CommonAreaTab from 'Components/Shared/Tab/CommonAreaTab'
import { CMCommissioningFilter } from 'Components/Filter'

import Menu from '../../Menu'
import TodoCard from './TodoCard'
import InProgressCard from './InProgressCard'
import FixFromQcCard from './FixFromQcCard'
import { getEvent } from 'features/wbs/wbs.selector'

const breadcrumbs: BreadcrumbLink[] = [
  {
    url: '/',
    title: 'Home',
    icon: '/home.png',
    active: true,
    showTitle: false,
  },
  {
    url: '/',
    title: translate(TKeys.Breadcrumb.dashboard),
    active: false,
    showTitle: false,
  },
]

type Props = ReduxProps & DispatchProps & RouteComponentProps

const CommissioningDashboard: React.ComponentType<Props> = props => {
  const {
    data,
    event,
    sorting,
    token,
    ProjectCode,
    UserEmail,
    sortingTask,
    filter,
    wbsEvent,
  } = props

  const dispatch = useDispatch()
  useEffect(() => {
    onGetData()
  }, [ProjectCode])

  const onGetData = () => {
    const data: GetDashboardReq = {
      ProjectCode,
      UserEmail,
      CommissioningTypeList: filter.CommissionTypeList,
      WbsList: filter.LeafWbsList,
    }

    dispatch(getCommissioningDashboard(token, data))
  }

  useEffect(() => {
    return () => {
      dispatch(clearFilter())
    }
  }, [])

  const onSearch = () => {
    onGetData()
  }

  const loading = (
    <div style={{ textAlign: 'center' }}>
      <Loading />
    </div>
  )

  const renderCard = (task: any[], type: string) => {
    let card = [] as any
    if (type === TypeOfTask.TODO) {
      card = task.map((activity, index) => (
        <TodoCard key={index} task={activity} />
      ))
    }

    if (type === TypeOfTask.InProgress) {
      card = task.map((activity, index) => (
        <InProgressCard key={index} task={activity} />
      ))
    }

    if (type === TypeOfTask.FixFromQC) {
      card = task.map((activity, index) => (
        <FixFromQcCard key={index} task={activity} />
      ))
    }

    return card
  }

  const renderTaskLen = (
    task: any[],
    header: string,
    orderName: string,
    type: string,
    total: number
  ) => {
    const card =
      task && task.length > 0 ? (
        renderCard(task, type)
      ) : (
        <NoCard text="ไม่มีรายการ" />
      )
    const content = event.IsLoading ? loading : card
    const title = `${header} (${total})`
    return (
      <Col span={8}>
        <FullColumn>
          <ColumnHeader
            heading={title}
            orderName={orderName}
            type={sorting[type]}
            onSort={() => {
              sortingTask(type)
            }}
          />
          {content}
        </FullColumn>
      </Col>
    )
  }

  return (
    <>
      <Menu selected="commonArea" />
      <SubnavBar breadcrumbs={breadcrumbs} hideSearch={true} />
      <CommonAreaTab selected="Commissioning" />
      <CMCommissioningFilter onSearch={onSearch} withPadding={true} />
      <LoadingView isShow={event.IsLoading || wbsEvent.IsLoading} />
      <MainPage>
        <Row type="flex">
          {renderTaskLen(
            data.Todo,
            'สิ่งที่ต้องทำ',
            'ระบบ',
            TypeOfTask.TODO,
            data.TotalTodo
          )}
          {renderTaskLen(
            data.InProgress,
            'QC กำลังดำเนินการ',
            'ล่าสุด',
            TypeOfTask.InProgress,
            data.TotalInProgress
          )}
          {renderTaskLen(
            data.FixFromQC,
            'แก้ไขจาก QC',
            'ระบบ',
            TypeOfTask.FixFromQC,
            data.TotalFixFromQC
          )}
        </Row>
      </MainPage>
    </>
  )
}

const MainPage = styled.div`
  padding: 0 120px;
  padding-top: 40px;
  background-color: #f5f6fa;
  width: 100vw;
  -khtml-user-select: none;
  -o-user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  user-select: none;
  overflow-x: scroll;
`

const FullColumn = styled.div`
  padding-right: 20px;
`

const mapStateToProps = (state: RootState) => {
  return {
    data: selectData(state),
    event: selectEvent(state),
    sorting: selectSorting(state),
    token: serviceTokenSelector(state),
    ProjectCode: selectedProjectCodeSelector(state),
    UserEmail: userEmailSelector(state),
    filter: selectFilter(state),
    wbsEvent: getEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

const mapDispatchToProps = {
  sortingTask: sortingDashboard,
}

type DispatchProps = typeof mapDispatchToProps

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CommissioningDashboard)
